import { Button, DefaultValue, Flex, getShadow, Spacing } from '@billy/design-system'
import { css } from '@emotion/core'
import { useTheme } from 'emotion-theming'
import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useOnClickOutside } from '../../../hooks'
import { Theme } from '../../../types/theme'
import { reactClass } from '../../../utils'

type FilterDropdownButtonProps = {
  disabled?: boolean
  dropdownOrigin?: 'left' | 'right'
}

export const FilterDropdownButton = (props: React.PropsWithChildren<FilterDropdownButtonProps>) => {
  const { disabled, dropdownOrigin, children, ...rest } = {
    dropdownOrigin: 'left',
    ...props,
  }

  const { t } = useTranslation()
  const theme = useTheme<Theme>()
  const { zIndex } = theme

  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLDivElement | null>(null)

  const handleOpen = useCallback(() => {
    if (!disabled) {
      setOpen(!open)
    }
  }, [disabled, open])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  useOnClickOutside(buttonRef, handleClose)

  return (
    <Flex
      className={reactClass('filter-dropdown-button')}
      justifyContent={`flex-${dropdownOrigin === 'left' ? 'start' : 'end'}`}
      css={css`
        position: relative;
      `}
      {...rest}
    >
      <div ref={buttonRef}>
        <Button iconRight="funnel" onClick={handleOpen} variant="outlined">
          {t('bankreconciliation.reconcilablepostings.filter')}
        </Button>
        <Flex
          css={css`
            position: absolute;
            top: calc(100% + ${Spacing.S8});
            ${dropdownOrigin}: 0px;
            min-width: 110px;
            padding: ${Spacing.S8};
            flex-direction: column;
            align-items: stretch;
            justify-items: flex-start;
            background-color: white;
            border-radius: ${DefaultValue.BorderRadius};
            z-index: ${zIndex.navigation};
            opacity: ${open ? 1 : 0};
            pointer-events: ${open ? 'auto' : 'none'};
            ${getShadow('elevatedStrong')};
          `}
        >
          {children}
        </Flex>
      </div>
    </Flex>
  )
}
